import { Box } from "@mui/material";
import {
  Icon,
  paletteVar,
  paletteVarAlpha,
  Typography,
} from "@platform/design-system";

import type { FC } from "react";
import type {
  EnrollmentMeasure,
  EnrollmentReviewRow,
} from "../../utils/quotedEnrollmentReview";

const FLAGGED_ROW_TINT = paletteVarAlpha(
  paletteVar("error-main", "#D32F2F"),
  0.14,
);

const LABEL_BACKGROUND = "action.hover";
const LABEL_HEADING = "# of Tiers";

const VOLUME_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const MEASURE_HEADING: Record<EnrollmentMeasure, string> = {
  lives: "Enrolled",
  volume: "Volume",
};

/** A plan can mix volume-based and unit-based rates, so the heading names every measure shown. */
const headingOf = (rows: readonly EnrollmentReviewRow[]): string =>
  [...new Set(rows.map((row) => MEASURE_HEADING[row.measure]))].join(" / ");

const formatFigure = (value: number, measure: EnrollmentMeasure): string =>
  measure === "volume" ? VOLUME_FORMAT.format(value) : String(value);

/** "+30%" above Current's, "-10%" below it; nothing where Current's figure is missing. */
const formatDifference = (percent: number | undefined): string =>
  percent === undefined ? "" : `${percent > 0 ? "+" : ""}${percent}%`;

export interface EnrollmentTableProps {
  side: "current" | "carrier";
  title: string;
  rows: readonly EnrollmentReviewRow[];
}

/**
 * One side of the comparison. Current's side only lists figures; the carrier's side also
 * marks each row that differs from Current's, with how far.
 */
export const EnrollmentTable: FC<EnrollmentTableProps> = ({
  side,
  title,
  rows,
}) => {
  const titleIsFlagged = side === "carrier" && rows.some((row) => row.flagged);

  return (
    <Box data-testid={`enrollment-table-${side}`} sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="header-medium"
        component="h3"
        color={titleIsFlagged ? "inherit" : "textSecondary"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: titleIsFlagged ? "warning.main" : undefined,
        }}
      >
        {titleIsFlagged && <Icon name="warning" color="warning" />}
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "3fr 2fr",
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ px: 1, py: 1.5, backgroundColor: LABEL_BACKGROUND }}>
          {LABEL_HEADING}
        </Box>
        <Box
          sx={{
            px: 1,
            py: 1.5,
            borderLeft: "1px solid",
            borderColor: "divider",
          }}
        >
          {headingOf(rows)}
        </Box>
        {rows.map((row) => {
          const markFlagged = side === "carrier" && row.flagged;
          const figure = side === "carrier" ? row.carrier : row.current;

          return (
            <Box
              key={row.key}
              data-testid="enrollment-row"
              sx={{ display: "contents" }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  backgroundColor: LABEL_BACKGROUND,
                }}
              >
                {row.label}
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 1.5,
                  borderTop: "1px solid",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  backgroundColor: markFlagged ? FLAGGED_ROW_TINT : undefined,
                }}
              >
                {markFlagged && (
                  <Box
                    data-testid="enrollment-row-flag"
                    sx={{ display: "flex", mr: "auto" }}
                  >
                    <Icon
                      name="warning"
                      color="error"
                      aria-label="Differs from current enrollment"
                    />
                  </Box>
                )}
                {figure === undefined ? "" : formatFigure(figure, row.measure)}
                {markFlagged && (
                  <Typography
                    variant="caption"
                    component="span"
                    color="danger"
                    sx={{ ml: "auto" }}
                  >
                    {formatDifference(row.differencePercent)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
